import CameraControls from "camera-controls";
import { Vector3 } from "three";

import { Context } from "../../../context";

import { SmoothTimes } from "../../controller";

import { ControllerStrategy } from "../strategy";

import { SpinControllerStrategy } from "./spin";
import { TrackingControllerStrategy } from "./tracking";
import { disableContextMenu } from "../../../../lib/misc/util";

export class PairControllerStrategy extends TrackingControllerStrategy {
  private target: Vector3 = new Vector3();
  private pos: Vector3 = new Vector3();
  private dir: Vector3 = new Vector3();

  private posOffset = new Vector3(0, 5, 0);
  private targetOffset = new Vector3(0, 1, 0);

  private maxTargetHeight = 10;
  private minPosHt = 1;
  private maxPosHt = 10;

  private maxInclination = (15 * Math.PI) / 180;

  private zooming = false;
  private zoomTimeoutMs = 3000;
  private zoomSpeedFactor = 0.33;
  private wheelScrollCnt = 0;
  private manualZoomDistance = 0;

  private cachedActions: {
    right: any;
    wheel: any;
    touch: {
      two: any;
      three: any;
    };
  } = {
    right: CameraControls.ACTION.NONE,
    wheel: CameraControls.ACTION.NONE,
    touch: {
      two: CameraControls.ACTION.NONE,
      three: CameraControls.ACTION.NONE,
    },
  };

  private cachedSmoothTime = SmoothTimes.DEFAULT;

  private pointerDownCb = (ev: PointerEvent) => {
    // ignore right clicks
    if (ev.button == 2) {
      return;
    }

    // ev.pointerType
    this.registered?.addAuxStrategy(SpinControllerStrategy);
    if (this.registered) {
      this.registered.state.following = false;

      if (this.registered.controls) {
        this.cachedSmoothTime = this.registered.controls.smoothTime;
        this.registered.controls.smoothTime = SmoothTimes.MEDIUM;
      }
    }
  };
  private pointerUpCb = (ev?: PointerEvent) => {
    if (ev && ev.button == 2) {
      return;
    }
    this.registered?.removeAuxStrategy(SpinControllerStrategy);
    if (this.registered) {
      this.registered.state.following = true;

      // TODO: improve timeout with update to newer camera controls
      setTimeout(() => {
        const controls = this.registered?.controls;
        if (controls) {
          controls.smoothTime = this.cachedSmoothTime;
          // TODO: support return to zoom home on mobile
          // controls.zoomTo(1, true);
        }
      }, 2000);
    }
  };

  private manualZoomCb = (deltaY: number) => {
    if (!this.registered) return;

    this.zooming = true;
    this.wheelScrollCnt++;

    this.manualZoomDistance -=
      deltaY *
      this.registered.state.zoomConfig.dolly.speed *
      this.zoomSpeedFactor;

    setTimeout(() => {
      this.wheelScrollCnt = Math.max(this.wheelScrollCnt - 1, 0);
      if (this.wheelScrollCnt == 0) {
        this.zooming = false;
        this.manualZoomDistance = 0;
      }
    }, this.zoomTimeoutMs);
  };
  private mouseZoomCb = (e: WheelEvent) => this.manualZoomCb(e.deltaY);

  init(context: Context): ControllerStrategy {
    super.init(context);

    if (!this.registered) {
      console.error("no controller registered");
      return this;
    }

    this.registered.state.following = true;

    const { controls, domRef } = this.registered;

    if (controls) {
      controls.getPosition(this.pos);
      controls.getTarget(this.target);

      this.dir
        .subVectors(this.target, this.pos)
        .setLength(this.registered.state.camDist);

      this.cachedActions.right = controls.mouseButtons.right;
      this.cachedActions.wheel = controls.mouseButtons.wheel;
      this.cachedActions.touch.two = controls.touches.two;
      this.cachedActions.touch.three = controls.touches.three;

      controls.mouseButtons.right = CameraControls.ACTION.NONE;
      controls.mouseButtons.wheel = CameraControls.ACTION.NONE;

      controls.touches.two = CameraControls.ACTION.NONE;
      controls.touches.three = CameraControls.ACTION.NONE;
    } else {
      console.error("no controls to take over");
    }

    domRef.addEventListener("contextmenu", disableContextMenu);

    domRef.addEventListener("wheel", this.mouseZoomCb);
    domRef.addEventListener("pointerdown", this.pointerDownCb);
    domRef.addEventListener("pointerup", this.pointerUpCb);
    domRef.addEventListener("pointerleave", this.pointerUpCb);

    return this;
  }

  update(_deltaS: number, _gameTimeDelta: number, frameNum?: number) {
    const controls = this.registered?.controls;

    if (!this.registered || !controls) {
      return;
    }

    if (this.registered.state.following) {
      const tracked = this.registered?.state.tracked;
      if (!tracked) {
        return;
      }
      const other = this.registered?.state.trackedOther;

      const softTransition = !this.registered.state.teleport;
      if (!softTransition) {
        this.registered.state.teleport = false;
      }

      this.target.addVectors(tracked.position, this.targetOffset);
      let positioned = false;

      let maxZoomDist = 0;
      let minZoomDist = -this.registered.state.zoomConfig.dolly.max;

      if (other) {
        let h = new Vector3()
          .subVectors(other.position, tracked.position)
          .length();
        let boundedOther = other.position
          .clone()
          .setY(Math.min(h * Math.sin(this.maxInclination), other.position.y));

        this.dir
          .subVectors(boundedOther, tracked.position)
          .setLength(this.registered.state.camDist);

        this.pos.subVectors(this.target, this.dir).add(this.posOffset);

        this.target.set(
          this.target.x,
          Math.min(this.target.y, this.maxTargetHeight),
          this.target.z
        );
        this.pos.set(
          this.pos.x,
          Math.min(this.maxPosHt, Math.max(this.minPosHt, this.pos.y)),
          this.pos.z
        );

        maxZoomDist =
          this.pos.distanceTo(this.target) -
          this.registered.state.zoomConfig.dolly.min;

        positioned = true;
      } else if (this.registered.state.homePos) {
        this.target.set(
          this.target.x,
          Math.min(this.target.y, this.maxTargetHeight),
          this.target.z
        );
        this.pos.set(
          this.registered.state.homePos.x,
          this.registered.state.homePos.y,
          this.registered.state.homePos.z
        );

        // TODO: replace this magic number with something more robust
        //       (currently makes it so the user doesn't zoom past pitcher
        //        in personal / pitcher cam)
        maxZoomDist = 20;

        positioned = true;
      }

      if (positioned) {
        if (this.zooming) {
          // do zoom explicitly - since we own the position of the camera
          // in this strategy
          this.dir.subVectors(this.target, this.pos).normalize();

          if (this.manualZoomDistance > maxZoomDist) {
            this.manualZoomDistance = maxZoomDist;
          } else if (this.manualZoomDistance < minZoomDist) {
            this.manualZoomDistance = minZoomDist;
          }

          this.dir.multiplyScalar(this.manualZoomDistance);

          this.pos.add(this.dir);
        }

        controls.setTarget(
          this.target.x,
          this.target.y,
          this.target.z,
          softTransition
        );
        controls.setPosition(
          this.pos.x,
          this.pos.y,
          this.pos.z,
          softTransition
        );
      } else {
        controls.moveTo(
          this.target.x,
          Math.min(this.target.y, this.maxTargetHeight),
          this.target.z,
          softTransition
        );
      }
    }

    if (frameNum != undefined) this.lastFrameNum = frameNum;
  }

  teardown() {
    super.teardown();

    if (!this.registered) return;

    const { controls, domRef } = this.registered;
    if (controls) {
      controls.setPosition(this.pos.x, this.pos.y, this.pos.z, true);
      controls.setTarget(this.target.x, this.target.y, this.target.z, true);

      controls.mouseButtons.right = this.cachedActions.right;
      controls.mouseButtons.wheel = this.cachedActions.wheel;

      controls.touches.two = this.cachedActions.touch.two;
      controls.touches.three = this.cachedActions.touch.three;
    }

    if (!this.registered.state.following) {
      this.pointerUpCb();
    }

    this.registered.state.tracked = undefined;
    this.registered.state.trackedOther = undefined;

    this.registered?.removeAuxStrategy(SpinControllerStrategy);

    domRef.removeEventListener("contextmenu", disableContextMenu);
    domRef.removeEventListener("wheel", this.mouseZoomCb);
    domRef.removeEventListener("pointerdown", this.pointerDownCb);
    domRef.removeEventListener("pointerup", this.pointerUpCb);
    domRef.removeEventListener("pointerleave", this.pointerUpCb);
  }
}
